define((require) => {
    "use strict";

    var _            = require("underscore"),
        ImprovedView = require("editor/zbone/ImprovedView"),
        template     = require("ui/common/ThrottleView.tpl"),
        DateUtil     = require("shared/util/DateUtil"),
        Cookie       = require("editor/util/Cookie");

    /**
     * @private @type {String}
     * The prefix for qualified cookie names.
     */
    var LOCKOUT = "lockout:";
    /**
     * @private @type {Number}
     * The default number of seconds to sleep between each view update, i.e. for ticking minutes during
     * a lockout.
     */
    var SLEEP = 6;
    /**
     * @private @type {Number}
     * The default number of seconds to lockout.
     */
    var DEFAULT_LOCKOUT_TIME = 5 * 60;

    /**
     * View for displaying a message about requests to a given (target, ip) being throttled, for example on
     * lockout because too many invalid credentials on the sign in view within a short time. <p>
     *
     * The view maintains a lockout timer internally, backed by cookies for the (target, ip) locked out in question.
     * When the target changes, the existing timer is cancelled, but the remaining lockout time is stored in a cookie,
     * to be restarted when the target changes back again. <p>
     *
     * On lockout start or cancel, this view will raise the "lockout" event with two arguments:
     * (lockout:Boolean, name:String). On lockout start, the <tt>lockout</tt> argument will be true,
     * and false on lockout cancel. The <tt>name</tt> is the qualified target in question.
     *
     * @class ThrottleView
     * @constructor
     *
     * @cfg {String}           [lockout]         The message to display during lockout; defaults to "You have made too
     *                                           many requests". Supported tags: $target and $minutes.
     * @cfg {String}           [retryAfter]      The text informing the user to "Please try again in $minutes minutes",
     *                                           which is the default. Supported tags: $target and $minutes.
     * @cfg {String}           [name]            Optional view name to include in the cookie name to distinguish it from
     *                                           other throttled views, i.e. if they have overlapping target values.
     * @cfg {jQuery}           [target]          An optional jQuery element supplying the current target to listen for.
     * @cfg {Function}         [validate]        A function to return true when the value from <tt>options.target</tt>
     *                                           is a valid target, for example an email validator to avoid checking
     *                                           targets that are not valid emails. Ignore if <tt>options.target</tt>
     *                                           is not specified.
     * @cfg {SequentialButton} [button]          A sequential button to disable, if any, while throttling is in effect,
     *                                           for example the "Sign In" button on the Sign In view.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    return ImprovedView.extend({

        /**
         * @private @type {{target:String, timer:Number}}
         * The current lockout timer ticking while lockout is in effect for a given target. <p>
         *
         * The old timer is cleared once a new target is used. The old timer can then be restarted again when the
         * target changes back, if ever, based on the remaining time stored in a cookie.
         */
        _lockoutTimer: null,
        /**
         * @private @type {Function}
         * A jQuery handler added to <tt>this.options.target</tt> to listen for "change" to update this view (optional).
         */
        _changeHandler: null,
        /**
         * @private @type {Function}
         * A jQuery handler added to <tt>this.options.target</tt> to listen for "keyup" to update this view (optional).
         */
        _keyUpHandler:  null,

        template: template,

        ui: {
            throttle:   ".throttle",
            lockout:    ".lockout",
            retryAfter: ".retry-after"
        },

        /**
         * @private
         * Returns the cookie name for the specified target.
         *
         * @param {String} target The target; cannot be falsey.
         * @return {String} The qualified cookie name; never falsey.
         */
        _name: function(target) {
            var name = LOCKOUT;
            if (this.options.name) {
                name += this.options.name + ":";
            }
            return name + target;
        },

        /**
         * @private
         * Returns the current (valid) target value from the configured dom input element to supply target
         * values to handle throttling for.
         *
         * @return {String} The current (valid) target, or null.
         */
        _target: function() {
            var target = this.options.target && this.options.target.val();
            return !this.options.validate || this.options.validate(target) ? target : null;
        },

        /**
         * @private
         * Updates this view based on the specified target and seconds remaining of lockout.
         *
         * @param {String} target  The target in lockout; never falsey.
         * @param {Number} seconds The seconds remaining of the lockout for <tt>target</tt>; when <= 1, this view
         *                         will be cleared.
         *
         */
        _updateView: function(target, seconds) {
            if (!_.isNumber(seconds) || seconds < 1) {
                this._clearView();
                return;
            }

            var ms = Math.ceil(seconds / 60) * 60 * 1000,
                minutes = DateUtil.timeDifference(ms),
                inject = function(text) {
                    return text.replace(/\$target/g, target).replace(/\$minutes/g, minutes);
                };

            // Set lockout message...
            this.ui.lockout.text(inject(this.options.lockout || "You have made too many requests."));
            // Set retry after message...
            this.ui.retryAfter.text(inject(this.options.retryAfter || "Please try again in $minutes."));
            // Show!
            this.ui.throttle.show();
        },

        /**
         * @private
         * Clears this view altogether (but not closed).
         */
        _clearView: function() {
            if (!this.isClosed) {
                this.ui.lockout.text("");
                this.ui.retryAfter.text("");
                this.ui.throttle.hide();
            }
        },

        //

        /**
         * @private
         * Clears the current running lockout timer, if any.
         */
        _clearLockoutTimer: function() {
            if (this._lockoutTimer) {
                clearTimeout(this._lockoutTimer);
                this._lockoutTimer = null;
            }
        },

        /**
         * @private
         * Sets the lockout timer to the number of specified seconds for the specified target; when elapsed,
         * the view will be cleared (no lockout message) and the configured sequential button, if any, will be
         * activated again. <p>
         *
         * @param {String} target  The target to set/clear the timer for; cannot be falsey.
         * @param {Number} seconds The number of seconds remaining of the lockout.
         *
         * @see #_clearLockoutTimer
         */
        _setLockoutTimer: function(target, seconds) {
            // Lets run every 6 seconds (10 times a minute), so we do not skew the minute tick too much...
            seconds -= SLEEP;

            if (seconds < 1) {
                this.cancelLockout(target);
                return;
            }

            // Update view...
            this._updateView(target, seconds);

            // Same timestamp, but ensure it reflects the seconds left. This in conjunction with the
            // cookie life time is sufficient...
            Cookie.set(this._name(target), Date.now() + "_" + seconds, seconds / (60 * 60));

            this._lockoutTimer = setTimeout(() => {
                this._setLockoutTimer(target, seconds);
            }, SLEEP * 1000);
        },

        // PUBLIC

        /**
         * Clears the lockout for the specified target.
         *
         * @param {String}  target       The target to cancel the lockout for; cannot be falsey.
         * @param {Boolean} [keepCookie] True to keep the cookie for the current target, false otherwise (default).
         */
        cancelLockout: function(target, keepCookie) {
            var name = this._name(target);
            if (!keepCookie) {
                Cookie.remove(name);
            }
            this._clearLockoutTimer();
            this._clearView();

            // Re-enable button...
            if (this.options.button) {
                this.options.button.enable(true);
            }
            // Trigger event...
            this.trigger("lockout", false, name);
        },

        /**
         * Checks to see if the supplied target has a cookie for lockout; if so, the lockout timer will
         * be started accordingly.
         *
         * @param  {String} target The target to check; cannot be falsey.
         * @return {Boolean} True if the timer has started, false otherwise.
         */
        checkLockout: function(target) {
            var name = this._name(target),
                lockout = Cookie.get(name),
                timer = false;

            if (lockout) {
                // format: 'time since the epoch in ms' + "_" + 'seconds remaining of lockout'
                lockout = lockout.split("_", 2);
                var time = parseInt(lockout[0], 10),
                    seconds = parseInt(lockout[1], 10);

                if (!(_.isNaN(time) && _.isNaN(seconds))) {
                    // Start timer with remaining seconds...
                    seconds -= (Date.now() - time) / 1000;
                    if (seconds > 1) {
                        this.startLockout(target, seconds); // will also disable the configured button, if any...
                        timer = true;
                    }
                }
            }
            if (!timer) {
                this.cancelLockout(target);
            }
            return timer;
        },

        /**
         * @private
         * Returns the deduced xhr response from the supplied arguments, which is either an xhr as-is or an
         * <tt>Arguments</tt> object storing the response.
         *
         * @param  {Arguments|*} xhr The xhr response as-is, or an <tt>Arguments</tt> array storing it; a falsey value
         *                           will return null.
         * @return {*} The deduced xhr response, or null if not found.
         */
        _response: function(xhr) {
            if (xhr) {
                if (typeof xhr.status !== "undefined") { // can actually be 0 on client errors
                    return xhr;
                }
                var i,
                    v; // Arguments

                for (i = 0; i < xhr.length; i++) {
                    v = xhr[i];
                    if (v && typeof v.status !== "undefined") {
                        return v;
                    }
                }
            }
            return null;
        },

        /**
         * Checks the supplied http response to see if the status code indicates throttling (429), in which case
         * a lockout is started for the supplied target. Any other status code will cause a cancelling of the
         * lockout. <p>
         *
         * This function is typically called from an ajax deferred <tt>done()</tt>, <tt>fail()</tt>, and/or
         * <tt>always()</tt> handler. These hooks supply the xhr response in different order, which is why the
         * entire <tt>Arguments</tt> array can be supplied from where the xhr response will be found. <p>
         *
         * Example: consider a view that uses this throttle view to display lockout messages, stored in the private
         * variable <tt>_throttleView</tt> shown in some region. Then, when a potentially throttle API endpoint is
         * called via ajax, call this function in the <tt>always()</tt> handler like:
         * <pre>
         *   ..
         *   // Some view using this throttle view in some region...
         *   this._throttleView = ..; // assumed setup onRender/onShow, one-time thing...
         *   ..
         *
         *   // Potentially throttled endpoint...
         *   this._callApi = function (target, ..) {
         *      // Assume "target" is used in ajax request, for example an email...
         *      return $.ajax({..})
         *         .done(..)
         *         .fail(..)
         *         .always(function () {
         *            if (this._throttleView.checkResponse(target, arguments)) {
         *               // optionally do stuff if throttling/lockout is in effect...
         *               ..
         *            }
         *         }.bind(this));
         *   };
         *   ..
         * </pre>
         *
         * @param  {String}              target The target to check; cannot be falsey.
         * @param  {Promise|Arguments|*} xhr    The response as-is, or the the arguments to find the xhr response in,
         *                                      e.g. from a deferred handler like <tt>always()</tt>, from which the
         *                                      status code will be checked for throttling in effect (429); cannot be
         *                                      falsey.
         * @return {Boolean} True if a lockout has now been started, false otherwise.
         */
        checkResponse: function(target, xhr) {
            // xhr be something else entirely on chained/nested deferreds, for example a resolved principal. So,
            // if we cannot find the request, we assume success since an actual value is resolved...
            xhr = this._response(xhr);
            if (!xhr || xhr.status !== 429) {
                this.cancelLockout(target, true);
                return false;
            }
            this.startLockout(target, parseInt(xhr.getResponseHeader("Retry-After"), 10));
            return true;
        },


        /**
         * Adds an <tt>always()</tt> callback to the supplied ajax promise that will check the http response to see
         * if the status code indicates throttling (429), in which case a lockout is started for the supplied target.
         * Any other status code will cause a cancelling of the lockout. <p>
         *
         * The response status code is checked using <tt>checkResponse</tt>.
         *
         * @param  {String}  target  The target to check; cannot be falsey.
         * @param  {Promise} promise The ajax promise to eventually check the response for; cannot be falsey.
         * @return {Promise} <tt>promise</tt>, now with an <tt>always()</tt> callback that will check the response.
         */
        checkPromise: function(target, promise) {
            return promise.always(function() {
                return this.checkResponse(target, arguments);
            }.bind(this));
        },

        /**
         * Starts a lockout for the specified target for the specified number of seconds.
         *
         * @param {String} target    The target to start the lockout for; cannot be falsey.
         * @param {Number} [seconds] The lockout time in seconds; will default to 300 seconds, i.e. 5 minutes.
         */
        startLockout: function(target, seconds) {
            if (this.options.button) {
                this.options.button.enable(false);
            }
            // Trigger event...
            this.trigger("lockout", true, this._name(target));

            this._setLockoutTimer(target, seconds || DEFAULT_LOCKOUT_TIME);
        },

        //

        onRender: function() {
            this._clearView();

            if (this.options.target) {
                this._changeHandler = function() {
                    var target = this._target();
                    if (target) {
                        this.checkLockout(target);
                    }
                }.bind(this);

                this._keyUpHandler = function() {
                    var target = this._target();
                    if (target) {
                        this.cancelLockout(target, true); // keep cookie
                    }
                }.bind(this);

                this.options.target.on("change", this._changeHandler);
                this.options.target.on("keyup",  this._keyUpHandler);

                // Check initial target value, if any...
                var target = this._target();
                if (target) {
                    this.checkLockout(target);
                }
            }
        },

        /**
         * Closes this view, clearing any running lockout timer.
         */
        onClose: function() {
            this._clearLockoutTimer();
            if (this._changeHandler) {
                this.options.target.off("change", this._changeHandler);
                this._changeHandler = null;
            }
            if (this._keyUpHandler) {
                this.options.target.off("keyup", this._keyUpHandler);
                this._keyUpHandler = null;
            }
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/common/ThrottleView.js